import { useCallback, useEffect, useMemo, useState } from 'react';

import { getRightPanelRecommendations } from '@atlassian/search-client';

import { logger } from '../../common/utils/logger';

export type Use3pRecommendedAppsProps = {
	userId?: string | null;
	enabled?: boolean;
};

export type Use3pRecommendedAppsResult = {
	recommendedApps: string[];
	loading: boolean;
	error: Error | null;
};

/**
 * Hook to fetch recommended 3rd party apps for a user.
 *
 * @param userId - The user ID to fetch recommendations for
 * @returns An object containing the recommended apps, loading state, and an error
 */
export function use3pRecommendedApps({
	userId,
	enabled = true,
}: Use3pRecommendedAppsProps): Use3pRecommendedAppsResult {
	const [recommendedApps, setRecommendedApps] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const fetchRecommendations = useCallback(async () => {
		if (!userId || !enabled) {
			setRecommendedApps([]);
			setLoading(false);
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const recommendations = await getRightPanelRecommendations({
				userId,
			});
			setRecommendedApps(Array.isArray(recommendations) ? recommendations : []);
		} catch (err) {
			setError(err instanceof Error ? err : new Error('Failed to fetch recommendations'));
			logger.error({ error: err, functionName: 'use3pRecommendedApps' });
			setRecommendedApps([]);
		} finally {
			setLoading(false);
		}
	}, [userId, enabled]);

	useEffect(() => {
		void fetchRecommendations();
	}, [fetchRecommendations]);

	return useMemo(
		() => ({
			recommendedApps,
			loading,
			error,
		}),
		[recommendedApps, loading, error],
	);
}
